'use client';

// The chrome that stays put: the password gate, the phone header, the two
// panels either side, and the frame every route draws into.
//
// It is a layout rather than part of a page, which is what makes moving between
// two views cheap — neither panel is re-rendered, and the follow loop in
// `console.tsx` above it is not interrupted. The routes supply `children` and
// nothing else.
//
// **The conversation lives here now, not at an address.** It used to be a route
// — `/chats/<id>` drew a page — which meant reading a document and holding a
// conversation were two places you could be, one at a time. They are not two
// places; they are the thing you are working on and the thing you are working
// with. So the wiki is on the left, what you are reading is in the middle, and
// the chat is on the right, and none of the three interrupts the others.
//
// What follows from that, and is the part worth not breaking: the chat pane is
// never unmounted. Closing it on a phone hides it in CSS. A half-typed message,
// the scroll position, and a turn streaming into the pane all survive being
// closed, reopened, and navigated across — which is what makes it a panel you
// can leave open rather than a page you have to go back to.
//
// The gate renders *instead of* everything on every route, at whatever address
// the browser asked for. No redirect and nothing to remember: a link opened cold
// is a password prompt, and unlocking it lands where you were going because the
// address never moved.

import { useEffect, useRef } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Cancel01Icon,
  DashboardSquare01Icon,
  Menu01Icon,
  Setting07Icon,
} from '@hugeicons/core-free-icons';
import { usePathname, useRouter } from 'next/navigation';
import { OpenDoc } from './markdown';
import { readDraftSpaces, stashedRail, useConsole, useMeasuredHeight } from './console';
import { AccountModal, Gate, WikiRows, WikiTreeSkeleton } from './parts';
import { Boot, useBootPhase } from './boot';
import { ChatDock } from './chats/dock';
import { storeToken } from '@/lib/token-store';

/* Fixtures are a wiki idea — a project directory that stands in for one that
   isn't real yet. Research has no projects and nothing to leave out, and a fresh
   `new Set()` in the render would be a new prop identity every pass. */
const NO_FIXTURES: Set<string> = new Set();

export function Shell({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    token, me, ready, error, setError, reconnecting, workspaces,
    chosen, drafting, tree, treeError, expanded, toggleDir, openPath, openSession,
    research, openResearch, fixtures, lowerThread,
    newChat, lock, menuOpen, setMenuOpen, closeMenu,
    menuButton, chatOpen, closeChat,
    accountOpen, setAccountOpen, topbarFloating,
  } = useConsole();

  // Both ends of the conversation float over it, and the conversation has to
  // know how much of itself is underneath each of them — see `.stream`, which
  // spends this as padding so that nothing at rest is ever covered.
  const topbar = useMeasuredHeight('--topbar-height');

  // The mark that stands in for all of this until it is worth showing — see
  // ./boot. Held over the server render and the wait for /api/me, then dissolved
  // over whichever of the gate and the shell turned out to be the answer.
  const boot = useBootPhase(ready);
  // Kept on for good once it has been applied. The entrance runs once and ends
  // on the element's own resting state, so leaving the class there costs
  // nothing — and taking it off at `gone` would cut the tail off an animation
  // that is deliberately longer than the veil's own fade.
  const arriving = boot !== 'held' ? ' is-arriving' : '';

  // Which document is selected, read off the address rather than tracked beside
  // it. Two sources for "where am I" is how a highlight ends up on a row you are
  // not looking at.
  const openPathname = pathname.startsWith('/wiki/')
    ? decodeURIComponent(pathname.slice('/wiki/'.length))
    : null;
  // The same question for the other shelf, and kept separate rather than folded
  // into one "which path" — the two roots can hold the same path (`README.md` is
  // in both) and a single value would light the row in whichever shelf drew
  // first.
  const openResearchPath = pathname.startsWith('/research/')
    ? decodeURIComponent(pathname.slice('/research/'.length))
    : null;
  const onBoard = pathname === '/';

  /*
   * Put the rail back: every conversation this browser was holding, in the
   * order it held them, each as the window or the pill it was.
   *
   * Once per load and never again, which is what `restored` is for. This is a
   * restore, not a subscription — after it has run, the rail on screen is the
   * truth and storage is only where it is written down, so re-reading would
   * mean a fold in this tab could be undone by a stale read in the next
   * render. `workspaces` has to be in before it can run at all — `resume`
   * needs it to say which repo a conversation belongs to — and arriving before
   * it lands is the ordinary case on a cold load, not an edge.
   *
   * This replaced three effects reading `chat`, `open` and `chatspace` off the
   * address, each with its own guard against re-firing when one of the others
   * changed. They were three because the address could only say one thing at a
   * time and the rail is several; one list in one place needs one pass.
   */
  const restored = useRef(false);

  useEffect(() => {
    if (!token || !workspaces.length) return;
    if (restored.current) return;
    restored.current = true;

    const rail = stashedRail();

    // The draft first, and synchronously — it is the only part of the rail
    // that needs nothing from the runner, so putting it back before the
    // conversations start arriving means the pane is never briefly holding it
    // by accident. `newChat` raises it and takes the pane; both are then
    // corrected against what the rail actually says.
    if (rail.draft && !drafting) {
      const key = newChat(readDraftSpaces(rail.draft.spaces, workspaces));
      if (!rail.draft.open) lowerThread(key);
    }

    // Then the conversations, in rail order, so the row comes back reading the
    // way it was left. Which one is in front is a flag on the matching entry
    // rather than a second pass afterwards: asking for the same conversation
    // twice is how a restore ends up holding two copies of it, and the one
    // that was in front is by definition one this loop is already asking for.
    //
    // Nothing takes the pane when the rail says the draft had it — no entry
    // matches `new`, so the front `newChat` just set is simply left alone.
    rail.threads.forEach((t, at) => {
      void openSession(t.id, { focus: t.id === rail.front, unfold: t.open, at });
    });
  }, [token, workspaces, drafting, openSession, newChat, lowerThread]);

  // Nothing else, not even hidden behind it: the gate would take focus into a
  // field nobody can see, and the shell would mount a header, a tree skeleton
  // and an empty board only to have them replaced the moment the data lands.
  if (boot === 'held') return <Boot />;

  if (!token) {
    return (
      <>
        <Gate
          arriving
          notice={error}
          onUnlock={t => {
            setError(null);
            storeToken(t);
          }}
        />
        {boot === 'leaving' && <Boot leaving />}
      </>
    );
  }

  // Instead of everything, exactly like the gate above it and for the same
  // reason: there is nothing here worth showing to somebody the app cannot yet
  // write to properly, and mounting the shell behind it would mean drawing a
  // board, a tree and a header in a register nobody has chosen.
  //
  // `me` has to have landed first. Before it does, `onboarded` is not false —
  // it is unknown, and treating unknown as false would show the question to
  // everybody for one frame of every load.
  if (me && !me.onboarded) return <Onboarding />;

  const closeBoth = () => {
    closeMenu();
    closeChat();
  };

  return (
    /* Everything below can contain a reply, and a reply can name a document.
       Provided once here rather than passed down, so no turn in the list has to
       carry a handler it does not use — see `OpenDoc` in ./markdown. */
    // A document named in a reply comes out of the repo the conversation is
    // working in, which is the first project chosen — the one the session
    // actually opened in. With none chosen there is no repo to read it from and
    // the wiki answers, which is what `openPath` does with no workspace.
    <OpenDoc.Provider value={path => openPath(path, chosen[0]?.name)}>
    <div
      className={`shell${menuOpen ? ' is-menu-open' : ''}${chatOpen ? ' is-chat-open' : ''}${arriving}`}
    >
      {/* Narrow screens only: the drawer's replacement in the layout.
          `traced` stays on in both states and the edge is faded by CSS instead
          of being mounted and unmounted here — see `.topbar::after`. */}
      <header
        className={`topbar traced${topbarFloating ? ' is-floating' : ''}`}
        ref={topbar}
      >
        {/* What is in the middle column, which is what this bar is the header
            for. It used to name the conversation, because the conversation was
            the page; the conversation is now a panel with its own head, and a
            bar naming something off to one side is a bar naming the wrong
            thing. */}
        <span className="topbar-title">
          <b>{onBoard ? 'Workspace' : (openPathname?.split('/').pop() ?? 'console')}</b>
        </span>

        {/* On the right, where the drawer's own close button is. The two are one
            control as far as anyone using it is concerned — press here, the
            panel opens; press there, it shuts — and a control that opens on the
            left and closes on the right is two controls to learn. The drawer
            still comes from the left, which is a thing you see happen rather
            than a thing you have to know in advance. */}
        <button
          ref={menuButton}
          className="btn-menu"
          onClick={() => {
            closeChat();
            setMenuOpen(o => !o);
          }}
          aria-label={menuOpen ? 'Close the wiki' : 'Open the wiki'}
          aria-expanded={menuOpen}
          aria-controls="wiki-drawer"
        >
          {/* A plain hamburger, and deliberately the least clever of the eleven
              on offer. `SidebarLeftIcon` describes what this actually does more
              precisely — it opens a drawer from the left — but precision is not
              what a menu button is for, and this is heading for people who have
              never had to learn an icon language. */}
          <HugeiconsIcon icon={Menu01Icon} size={19} strokeWidth={2} />
        </button>
      </header>

      {/* Tapping beside an open drawer closes it. Inert until then, so it never
          swallows a tap intended for the page. */}
      <div className="scrim" onClick={closeBoth} aria-hidden="true" />

      <aside className="sidebar traced" id="wiki-drawer">
        <div className="sidebar-head">
          {/* Whose console this is, at the top, where Notion and everything
              shaped like it puts the same thing. It was at the foot, pinned so a
              long conversation list could not push it out of reach. That list
              has moved to the other side of the screen and this panel holds the
              wiki now — a tree you scroll *down* — so the top is both where it
              is expected and where it is out of the way.

              It carries the drawer's close button on narrow screens, at the
              same end as the header's menu button — the header goes as this
              arrives, and the control that opened the panel is the control that
              shuts it, in the place the thumb left it. */}
          <div className="account-row">
            <button
              className="account"
              onClick={() => setAccountOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
            >
              <span className="avatar" aria-hidden="true">
                {(me?.label ?? '·').slice(0, 1)}
              </span>
              <span className="account-name">{me?.label ?? 'Signing in…'}</span>
              {/* A gear, not the `⋯` glyph it replaced — the same reasoning
                  as `.wiki-caret`: a decorative character is a font
                  dependency nothing here needs, and this app already draws
                  its icons rather than typing them. */}
              <span className="account-chevron" aria-hidden="true">
                <HugeiconsIcon icon={Setting07Icon} size={16} strokeWidth={1.8} />
              </span>
            </button>
            <button className="btn-close" onClick={closeMenu} aria-label="Close the wiki">
              <HugeiconsIcon icon={Cancel01Icon} size={18} strokeWidth={2} />
            </button>
          </div>

          {/* The front door, above the tree rather than inside it. The board is
              not a document and does not sit in the wiki's shape — it is what
              the wiki adds up to — so a row of its own above the files says that
              better than a root node would. */}
          <button
            className={`nav-row${onBoard ? ' is-active' : ''}`}
            onClick={() => {
              closeMenu();
              router.push('/');
            }}
          >
            {/* Same-width slot as `.avatar` above it — see the comment on
                `.row-lead` — so "Workspace" starts where "Rowan" does. */}
            <span className="row-lead">
              <HugeiconsIcon icon={DashboardSquare01Icon} size={17} strokeWidth={1.8} />
            </span>
            Workspace
          </button>
        </div>

        {/* The only region that scrolls, so the account row and the board stay
            put no matter how long the tree gets. */}
        <div className="sidebar-body">
          <nav className="wiki-tree">
            {treeError && <div className="muted">{treeError}</div>}
            {!treeError && tree === null && <WikiTreeSkeleton />}
            {tree?.length === 0 && <div className="muted">The wiki is empty.</div>}
            {tree && (
              <WikiRows
                nodes={tree}
                depth={0}
                expanded={expanded}
                onToggle={toggleDir}
                selected={openPathname}
                onSelect={node => openPath(node.path)}
                fixtures={fixtures}
              />
            )}

            {/* Scratch, under the wiki and inside the same tree rather than
                beside it. The whole point of this repo is that things get
                promoted out of it into the wiki above, and a shelf you have to
                switch to would make that copy feel like a migration between two
                systems instead of a move up the same list.

                No skeleton and no error row: research is allowed not to exist —
                see `research` in console.tsx — so its absence is the ordinary
                state rather than a thing that failed. It appears when there is
                something in it.

                Drawn at depth 1 so that a topic directory gets a caret instead
                of a second permanent heading. "Research" is the section; the
                topics under it are folders you open. */}
            {research && (
              <>
                <div className="wiki-section-label section-label">Research</div>
                <WikiRows
                  nodes={research}
                  depth={1}
                  expanded={expanded}
                  onToggle={toggleDir}
                  selected={openResearchPath}
                  onSelect={node => openResearch(node.path)}
                  fixtures={NO_FIXTURES}
                />
              </>
            )}
          </nav>
        </div>
      </aside>

      {/* Not `traced`: the vine ring is the panel's edge, and this column
          stopped being a panel — see `.main`. */}
      <main className="main">
        {error && <div className="banner">{error}</div>}
        {reconnecting && (
          <div className="banner banner-info">
            Reconnecting to this turn — it is still running on the server.
          </div>
        )}

        {children}
      </main>

      {/* Every conversation, folded or not. Outside every panel because it is
          fixed to the window and floats over all of them; last so it stacks
          above them without a z-index argument. */}
      <ChatDock />

      <AccountModal
        me={me}
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        onLogOut={() => {
          setAccountOpen(false);
          lock();
        }}
      />
    </div>

    {/* A sibling of the shell rather than a child of it, which is load-bearing:
        `.is-arriving` fades the shell up from nothing, and anything inside it
        would be faded up along with it — the veil would vanish on the frame it
        was meant to start dissolving on. */}
    {boot === 'leaving' && <Boot leaving />}
    </OpenDoc.Provider>
  );
}
